/**
 * 「用户与收入」三页共用的小部件：弹窗外壳、带说明的复选框、字段行、花瓣与时间换算。
 *
 * 背景：用户与计费、充值订单、激活码三页都有「弹窗里填表单、提交、失败留在弹窗」的交互，
 *   Alice 的弹窗是 .modal-mask / .modal 的内联结构；共用的 kit-runtime2.tsx 属于别的页面组，这里自己写。
 * 设计意图：
 *   - 弹窗 createPortal 到 body，Esc 关闭，提交中（busy）不可关闭；有输入框的弹窗不允许点遮罩关闭（防止填了一半丢失）。
 *   - 金额统一经 useP 换成花瓣显示：后端是整数 unit（1 花瓣 = 100,000 unit），页面不做浮点运算再发回去。
 * 关键约束：不改 kit.tsx 的既有导出；新增的类名在 users.css，以 u2- 开头。
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type InputHTMLAttributes, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { fmtPetals } from '@ap/api-client/money'
import { AG_users as G, errorTextUsers } from '@ap/i18n/admin-users'
import { intlTag, useI18n } from '@ap/i18n'
import './users.css'

/** 本组页面用的翻译与格式化：t、错误文案、花瓣显示。 */
export function useU() {
  const { t, locale } = useI18n()
  const tag = intlTag(locale)
  return useMemo(
    () => ({
      t,
      locale,
      tag,
      G,
      err: (e: unknown) => errorTextUsers(e, locale),
      /** unit 转「N 花瓣」的数字部分（不带单位）。 */
      num: (units: number | null | undefined) => (units === null || units === undefined ? '—' : fmtPetals(units, tag)),
      /** unit 转「N 花瓣」。 */
      pet: (units: number | null | undefined) => (units === null || units === undefined ? '—' : t(G.common.petals, { n: fmtPetals(units, tag) })),
      /** 带正负号的花瓣变动。 */
      signed: (units: number) => (units > 0 ? '+' : units < 0 ? '−' : '') + fmtPetals(Math.abs(units), tag),
    }),
    [t, locale, tag],
  )
}

/** 整数展示（请求数、Token 等）。 */
export function useInt() {
  const { locale } = useI18n()
  const tag = intlTag(locale)
  return (n: number | null | undefined) => Number(n ?? 0).toLocaleString(tag)
}

/** Token 的紧凑显示：1.2K、3.4M。 */
export function compact(n: number | null | undefined): string {
  const v = Number(n ?? 0)
  if (v >= 1e6) return `${(v / 1e6).toFixed(1)}M`
  if (v >= 1e3) return `${(v / 1e3).toFixed(1)}K`
  return String(v)
}

export const fieldStyle: CSSProperties = {
  padding: '8px 10px',
  fontSize: 13,
  background: 'var(--bg-input)',
  border: '1px solid var(--border-medium)',
  borderRadius: 6,
  color: 'var(--text-primary)',
  fontFamily: 'var(--font)',
}

/**
 * 弹窗外壳。Esc 关闭；busy 时 Esc 与关闭按钮都不生效；dismissOnMask 只给没有输入框的弹窗用。
 */
export function UModal({
  open,
  title,
  onClose,
  footer,
  busy = false,
  width = 560,
  dismissOnMask = false,
  children,
}: {
  open: boolean
  title: ReactNode
  onClose: () => void
  footer?: ReactNode
  busy?: boolean
  width?: number
  dismissOnMask?: boolean
  children: ReactNode
}) {
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  const busyRef = useRef(busy)
  busyRef.current = busy
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busyRef.current) closeRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])
  if (!open) return null
  return createPortal(
    <div
      className="modal-mask open"
      style={{ zIndex: 9000 }}
      onMouseDown={(e) => {
        if (dismissOnMask && !busy && e.target === e.currentTarget) onClose()
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" style={{ width, maxWidth: '96vw' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18, gap: 12 }}>
          <div className="modal-title" style={{ marginBottom: 0 }}>
            {title}
          </div>
          <button
            type="button"
            disabled={busy}
            onClick={onClose}
            aria-label="close"
            style={{ padding: '4px 8px', border: 'none', background: 'none', cursor: busy ? 'not-allowed' : 'pointer', color: 'var(--text-tertiary)', fontSize: 18 }}
          >
            ✕
          </button>
        </div>
        {children}
        {footer && <div className="modal-footer">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

/** 表单项：标签加输入，hint 是下方的小字说明。 */
export function FormRow({ label, hint, children }: { label: ReactNode; hint?: ReactNode; children: ReactNode }) {
  return (
    <div className="form-group">
      <label>{label}</label>
      {children}
      {hint && <div className="u2-note" style={{ marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

/** 带说明文字的复选框。 */
export function Check({
  checked,
  onChange,
  disabled,
  children,
  hint,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  disabled?: boolean
  children: ReactNode
  hint?: ReactNode
}) {
  return (
    <label className="u2-check" style={disabled ? { cursor: 'not-allowed' } : undefined}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} style={{ cursor: 'inherit' }} />
      <span>
        {children}
        {hint && <span style={{ color: 'var(--text-tertiary)' }}> {hint}</span>}
      </span>
    </label>
  )
}

/** 数字输入：只收整数文本，避免输入法与科学计数写法。 */
export function IntInput({ value, onChange, ...rest }: Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange' | 'value'> & { value: string; onChange: (v: string) => void }) {
  return <input {...rest} type="text" inputMode="numeric" value={value} onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, ''))} />
}

/** datetime-local 的值（本地时间）转 ISO 字符串；空值返回 undefined。 */
export function localToIso(v: string): string | undefined {
  if (!v) return undefined
  const d = new Date(v)
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString()
}

/** ISO 转 datetime-local 的值。 */
export function isoToLocal(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`
}

/** 从现在起 n 天后的 datetime-local 值。 */
export function inDays(n: number): string {
  return isoToLocal(new Date(Date.now() + n * 86_400_000).toISOString())
}

/**
 * 生成一次提交的请求号（后端要求 UUID）：同一次弹窗里重试沿用，后端据此幂等。
 * 约束：randomUUID 只在安全上下文可用，否则用 getRandomValues 拼 v4 UUID。
 */
export function newRef(): string {
  const c: Crypto = crypto
  if (typeof c.randomUUID === 'function') return c.randomUUID()
  const b = c.getRandomValues(new Uint8Array(16))
  b[6] = (b[6] & 0x0f) | 0x40
  b[8] = (b[8] & 0x3f) | 0x80
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('')
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`
}

/** 复制文本到剪贴板；失败时返回 false，由调用方提示。 */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (e) {
    console.warn('[ui] 复制失败', e)
    return false
  }
}

/**
 * 按需加载：enabled 为 false 时不请求（抽屉里没打开的标签页不发请求）；deps 变化重新请求，旧请求的结果丢弃。
 * 约束：失败只记录在 error，由页面显示；reload 用于写操作成功后刷新。
 */
export function useQuery<T>(fn: () => Promise<T>, deps: unknown[], enabled = true) {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(enabled)
  const [tick, setTick] = useState(0)
  useEffect(() => {
    if (!enabled) return
    let alive = true
    setLoading(true)
    setError(null)
    fn()
      .then((v) => alive && setData(v))
      .catch((e) => {
        if (!alive) return
        console.error('[g2] 加载失败', e)
        setError(e)
      })
      .finally(() => alive && setLoading(false))
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick, enabled])
  const reload = useCallback(() => setTick((x) => x + 1), [])
  return { data, error, loading, reload, setData }
}
